import { COLORS } from '@laerdal/life-react-components';
import React from 'react';
import styled from 'styled-components';

interface ProductListItemProps {
  icon?: React.ReactNode;
  imageUrl?: string;
  title: string;
  description?: string;
  onClick: () => void;
  titleSize?: number;
  titleWeight?: number;
  bold?: boolean;
}

const Container = styled.div`
  display: flex;
  min-height: 64px;
  padding: 4px;
  align-items: center;
  border-radius: 8px;
  border: 1px solid var(--Neutral-200-tint, #CCC);
  background: var(--Base-White, #FFF);
  cursor: pointer;

  &:hover {
    border-radius: 8px;
    border: 1px solid var(--Neutral-200-tint, #CCC);
    background: var(--Primary-020-tint, #F1FBFE);
  }

  &:active {
    border-radius: 8px;
    border: 1px solid var(--Neutral-200-tint, #CCC);
    background: var(--Primary-100-tint, #D4E9F2);
  }

`;

const ImageContainer = styled.div`
  display: flex;
  width: 96px;
  height: 72px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  border-radius: 4px;
  background: var(--Base-White, #FFF);
`;

const IconContainer = styled.div`
  display: flex;
  width: 96px;
  height: 72px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  padding: 0 8px;
`;

const Title = styled.div<{ $bold?: boolean }>`
  overflow: hidden;
  color: var(--Base-Black, #1F1F1F);
  font-feature-settings: 'liga' off;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  font-family: Lato;
  font-size: 16px;
  font-style: normal;
  font-weight: ${props => props.$bold ? 700 : 400};
  line-height: 24px;
`;

const Description = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  color: var(--Neutral-600-shade, #666);
  font-feature-settings: 'liga' off;
  font-family: Lato;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
`;

const ProductListItem: React.FC<ProductListItemProps> = ({
  icon,
  imageUrl,
  title,
  description,
  onClick,
  titleSize,
  titleWeight,
  bold
}) => {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <Container 
      onClick={onClick} 
      tabIndex={0}
      onKeyDown={handleKeyDown}
      role="button"
      aria-label={title}
    >
      {imageUrl ? (
        <ImageContainer>
          <Image src={imageUrl} alt={title} />
        </ImageContainer>
      ) : icon ? (
        <IconContainer>
          <IconWrapper>
            {icon}
          </IconWrapper>
        </IconContainer>
      ) : null}
      <TextContainer>
        <Title $bold={bold}>{title}</Title>
        {description && <Description>{description}</Description>}
      </TextContainer>
    </Container>
  );
};

export default ProductListItem;
